import { useEffect, useRef } from "react";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { useAuthContext } from "../auth/useAuthContext";
import { HOST_API_KEY } from "../config-global";
import axios from "../utils/axios";

// Module-level singleton — lets useGeminiVoiceOrdering publish without prop-drilling
let _publishFn: ((sessionId: string, steps: any[]) => void) | null = null;

export function publishConversationSnapshot(sessionId: string, steps: any[]) {
  _publishFn?.(sessionId, steps);
}

export default function useVoiceStatusSocket() {
  const { user } = useAuthContext();
  const clientRef = useRef<Client | null>(null);
  const sessionSubsRef = useRef<{ connected: any; released: any; updated: any; completed: any } | null>(null);
  const pendingSessionIdRef = useRef<string | null>(null);
  const activeSessionIdRef = useRef<string | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentOrderIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user) return undefined;
    if (clientRef.current) return undefined;

    const subOrgId = user?.subOrgId || "GLOBAL";
    const baseUrl = HOST_API_KEY || window.location.origin;
    const socketUrl = `${baseUrl.replace(/\/$/, "")}/ws`;

    const subscribeToSession = (stompClient: Client, sessionId: string) => {
      sessionSubsRef.current?.connected?.unsubscribe();
      sessionSubsRef.current?.released?.unsubscribe();
      sessionSubsRef.current?.updated?.unsubscribe();
      sessionSubsRef.current?.completed?.unsubscribe();

      const dispatch = (name: string, data: any) =>
        window.dispatchEvent(new CustomEvent(name, { detail: data }));

      const connectedSub = stompClient.subscribe(`/topic/agent-connected/${sessionId}`, (msg) => {
        stopPolling();
        try { dispatch('agent-connected', JSON.parse(msg.body)); } catch { dispatch('agent-connected', {}); }
      });

      const releasedSub = stompClient.subscribe(`/topic/agent-released/${sessionId}`, (msg) => {
        stopPolling();
        try { dispatch('agent-released', JSON.parse(msg.body)); } catch { dispatch('agent-released', {}); }
      });

      const updatedSub = stompClient.subscribe(`/topic/order-updated/${sessionId}`, (msg) => {
        try { dispatch('order-updated', JSON.parse(msg.body)); } catch { dispatch('order-updated', {}); }
      });

      const completedSub = stompClient.subscribe(`/topic/order-completed/${sessionId}`, (msg) => {
        try { dispatch('order-completed', JSON.parse(msg.body)); } catch { dispatch('order-completed', {}); }
      });

      sessionSubsRef.current = { connected: connectedSub, released: releasedSub, updated: updatedSub, completed: completedSub };
      activeSessionIdRef.current = sessionId;
      console.log(`🎯 VOICE_SOCKET: Subscribed to session topics for ${sessionId}`);
    };

    const stopPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };

    // Polling fallback: if STOMP missed the notification, poll the order status
    const startPolling = (orderId: string) => {
      stopPolling();
      pollIntervalRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`/api/v1/orders/${orderId}`);
          const order = res.data;
          if (order?.sessionStatus === 'AGENT_CONNECTED' || order?.assignedAgentId) {
            stopPolling();
            window.dispatchEvent(new CustomEvent('agent-connected', {
              detail: {
                agentName: '',
                orderId: order.id,
              }
            }));
          } else if (order?.sessionStatus === 'COMPLETED' || order?.sessionStatus === 'IDLE') {
            stopPolling();
          }
        } catch {
          // Ignore polling errors
        }
      }, 4000);
    };

    const client = new Client({
      webSocketFactory: () => new SockJS(socketUrl),
      connectHeaders: {
        subOrgId,
        Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}`,
      },
      debug: () => {},
      reconnectDelay: 3000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    client.onConnect = () => {
      console.log("🟢 VOICE_SOCKET: CONNECTED for org:", subOrgId);
      _publishFn = (sessionId: string, steps: any[]) => {
        if (client.connected) {
          client.publish({
            destination: '/app/conversation-relay',
            body: JSON.stringify({ sessionId, steps }),
          });
        }
      };
      // On reconnect, re-subscribe if we had an active session
      const sessionToSubscribe = pendingSessionIdRef.current || activeSessionIdRef.current;
      if (sessionToSubscribe) {
        subscribeToSession(client, sessionToSubscribe);
        pendingSessionIdRef.current = null;
      }
    };

    const handleSessionStarted = (event: Event) => {
      const { sessionId, orderId } = (event as CustomEvent).detail || {};
      if (!sessionId) return;
      if (orderId) currentOrderIdRef.current = String(orderId);
      if (client.connected) {
        subscribeToSession(client, sessionId);
      } else {
        pendingSessionIdRef.current = sessionId;
        activeSessionIdRef.current = sessionId;
      }
      // Start polling as a fallback in case STOMP drops
      if (orderId) startPolling(String(orderId));
    };

    window.addEventListener('voice-session-started', handleSessionStarted);

    client.onStompError = (frame: any) => {
      console.error(`🔴 VOICE_SOCKET: Broker error: ${frame.headers.message}`);
    };

    client.onWebSocketError = (event: any) => {
      console.error("🔴 VOICE_SOCKET: WebSocket error", event);
    };

    client.onWebSocketClose = () => {
      console.warn("🟠 VOICE_SOCKET: WebSocket closed, will reconnect...");
    };

    client.activate();
    clientRef.current = client;

    return () => {
      stopPolling();
      _publishFn = null;
      window.removeEventListener('voice-session-started', handleSessionStarted);
      if (clientRef.current) {
        clientRef.current.deactivate();
        clientRef.current = null;
      }
    };
  }, [user]);
}
